<template>
  <div
    v-if="selectedCountryData"
    class="wallet-topup-review"
  >
    <ReviewAndPayCard
      :recap-title="t('billing.buyCredits.cardTitle')"
      :recap-subtitle="`${flagEmoji(country)} ${countryLabelOf(country)}`"
      recap-icon="fa-regular fa-coins"
      :recap-lines="recapLines"
      :total-label="t('billing.review.totalToday')"
      :total-value="formatAmount(amount)"
      :payment-mode="paymentMode"
      :payment-method-id="paymentMethodId"
      :default-card="defaultCard"
      :all-cards="allCards"
      :amount="amount"
      :currency="currency"
      :allowed-methods="ALLOWED_METHODS_TOPUP"
      :dropin-submit-handler="dropinSubmitHandler"
      :dropin-details-handler="dropinDetailsHandler"
      :payment-error="paymentError"
      :cta-label="ctaLabel"
      cta-icon="fa-regular fa-lock"
      :cta-submitting="ctaSubmitting"
      :trust-text="t('billing.review.trustSecure')"
      @update:payment-mode="onPaymentModeChange"
      @update:payment-method-id="onPaymentMethodIdChange"
      @dropin-success="onDropinSuccess"
      @dropin-error="onDropinError"
      @cta-click="onCtaClick"
    />
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, toRef, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import ReviewAndPayCard, { ReviewAndPayLine } from '@/components/billing/shared/ReviewAndPayCard.vue';
import { ActionResult, ALLOWED_METHODS_TOPUP } from '@/composables/billing/billingApi';
import { useSmsCountries } from '@/composables/billing/useSmsCountries';
import {
  billingCurrencySymbol,
  formatBillingAmount,
} from '@/composables/billing/formatAmount';
import { PaymentMethodItem } from '@/types/billing-types';

type PaymentMode = 'existing' | 'new';

/**
 * Wallet top-up review step — the « Finaliser votre achat » view of the
 * BuyCredits flow. Thin orchestrator around {@link ReviewAndPayCard} :
 * computes the SMS-quantity / unit-price lines from `useSmsCountries`,
 * delegates the recap+payment layout to the shared card.
 */
export default defineComponent({
  name: 'WalletTopUpReview',

  components: { ReviewAndPayCard },

  props: {
    amount: {
      type: Number,
      required: true,
    },

    country: {
      type: String,
      required: true,
    },

    paymentMode: {
      type: String as PropType<PaymentMode>,
      required: true,
    },

    defaultCard: {
      type: Object as PropType<PaymentMethodItem | null>,
      default: null,
    },

    allCards: {
      type: Array as PropType<PaymentMethodItem[]>,
      default: () => [],
    },

    paymentMethodId: {
      type: String as PropType<string | null>,
      default: null,
    },

    paymentError: {
      type: String as PropType<string | null>,
      default: null,
    },

    /** Shop billing currency — derived from the overview by the orchestrator. */
    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },

    /** Drop-in handlers — provided by the orchestrator (BuyCreditsDialog). */
    dropinSubmitHandler: {
      type: Function as PropType<(state: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    dropinDetailsHandler: {
      type: Function as PropType<(state: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    /** In-card CTA — empty disables the button. */
    ctaLabel: {
      type: String,
      default: '',
    },

    ctaSubmitting: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:paymentMode',
    'update:paymentMethodId',
    'dropin-success',
    'dropin-error',
    'cta-click',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const sms = useSmsCountries('oneShoot');

    watch(
      () => props.country,
      (v) => { sms.selectedCountry.value = v; },
      { immediate: true },
    );

    const amountRef = toRef(props, 'amount');
    const estimatedSms = computed(() => sms.estimatedSmsCount(amountRef.value));

    const formatAmount = (n: number) => formatBillingAmount(n, props.currency);
    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);
    const formatUnitPrice = (n: number) => {
      const symbol = billingCurrencySymbol(props.currency);
      const value = n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
      return props.currency === 'USD' ? `${symbol}${value}` : `${value} ${symbol}`;
    };

    const recapLines = computed<ReviewAndPayLine[]>(() => {
      const data = sms.selectedCountryData.value;
      if (!data) return [];
      return [
        {
          label: t('billing.buyCredits.review.amountLine'),
          value: formatAmount(props.amount),
        },
        {
          label: t('billing.buyCredits.review.estimatedSmsLine'),
          value: `~ ${formatNumber(estimatedSms.value)} SMS`,
        },
        {
          label: t('billing.buyCredits.unitRateLabel'),
          value: `${formatUnitPrice(data.unitPriceOneShoot)} / SMS`,
        },
      ];
    });

    const onDropinSuccess = () => emit('dropin-success');
    const onDropinError = (err: string) => emit('dropin-error', err);
    const onPaymentModeChange = (mode: PaymentMode) => emit('update:paymentMode', mode);
    const onPaymentMethodIdChange = (id: string | null) => emit('update:paymentMethodId', id);
    const onCtaClick = () => emit('cta-click');

    return {
      t,
      ALLOWED_METHODS_TOPUP,
      selectedCountryData: sms.selectedCountryData,
      countryLabelOf: sms.countryLabelOf,
      flagEmoji: sms.flagEmoji,
      recapLines,
      formatAmount,
      onDropinSuccess,
      onDropinError,
      onPaymentModeChange,
      onPaymentMethodIdChange,
      onCtaClick,
    };
  },
});
</script>

<style lang="scss" scoped>
.wallet-topup-review {
  // Wrapper kept so the parent's `v-if="selectedCountryData"` placement is
  // preserved ; all the layout / theming lives in ReviewAndPayCard.
}
</style>
